import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Barnstormer set (ADR-0160 amended, mythic): an air-show stunt pilot from the days of biplanes. A leather flying
 * helmet with brass goggles and a propeller on its crown, a bomber jacket with a fleece collar, and the Sky Ace.
 *
 * The head is a mask over the whole head: a leather flying helmet (brown, lit from the top-left, a racing stripe in
 * the team's colour over its crown), its ear flap buckled under, two big round goggles in brass rims over its face, their lenses in the team's
 * light, a cream fleece muffler wound over its mouth and nose, and on its crown a brass hub. Lying, its lenses are cracked
 * crosses. Its flair: a red propeller with gold tips spinning on the hub (the bright moving part at 1:1, in a blur of its
 * own), a glint wiping across the lenses now and then.
 * The suit is a brown leather bomber jacket, a thick cream fleece collar, a zip down its front, a roundel in the team's
 * colours on its shoulder, a pair of brass wings pinned on its chest, a brown belt. Its flair: puffs of cloud left behind
 * it on the run, a glint on its wings.
 *
 * Rubric (`assetc mythic barnstormer` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside
 * Tin Toy, Piñata and Disco): head S4 R5 P4 L4 C4 A5 F4, jacket S4 R4 P4 L4 C4 F4. Weakest: the leather's browns sit
 * close to the lane's; the goggles and the propeller carry it there.
 */

// --- The flying helmet -----------------------------------------------------------------------------------------------

/** The helmet's dome (u forward, v down from the head's middle). */
const DOME: Ellipse = [0, -2, 19.5, 19];
/** The ear flap at its back-side, hanging under it. */
const FLAP: Ellipse = [-8, 9, 6.5, 8.5];
/** The goggles: [u, v, r]. */
const GOGGLES = [
  [5, -3, 6],
  [16.5, -3, 4.8],
] as const;
/** The muffler wound over its mouth: its box. */
const MUFFLER = [-4, 5, 22.5, 15.5] as const;
/** The brass hub on its crown. */
const HUB: Ellipse = [1, -21.2, 3.2, 1.8];

/** Leather at light `l`: deep brown to tan. */
const leather = (l: number): PaletteKey => (l > 0.86 ? 't' : l > 0.55 ? 'm' : l > 0.15 ? 'n' : 'N');

const helmet: HeadPicture = (u, v, lx, ly, lying) => {
  // The hub, its stem into the crown.
  if (inEllipse(u, v, HUB) <= 1) return v < HUB[1] ? 'y' : 'U';
  if (Math.abs(u - HUB[0]) < 1.2 && v > HUB[1] && v < -19.5) return 'Y';
  // The goggles: brass rims, the team's light in their lenses, a glint; a strap behind them round the helmet.
  for (const [gu, gv, r] of GOGGLES) {
    const [du, dv] = [u - gu, v - gv];
    const d = Math.hypot(du, dv);
    if (d > r) continue;
    if (d > r - 1.6) return du + dv < 0 ? 'y' : d > r - 0.7 ? 'n' : 'Y';
    if (lying) return Math.abs(Math.abs(du) - Math.abs(dv)) < 0.8 ? 'w' : 'B';
    if (Math.hypot(du + r * 0.35, dv + r * 0.35) < r * 0.25) return 'w';
    return du + dv > r * 0.5 ? 'B' : du + dv > -r * 0.2 ? 'b' : 'c';
  }
  // The bridge between them.
  if (u > 10.5 && u < 12.5 && Math.abs(v + 3.5) < 1) return 'Y';
  // The muffler: cream fleece in lumps, shaded under its folds.
  const [m0, m1, m2, m3] = MUFFLER;
  if (u >= m0 && u <= m2 && v >= m1 && v <= m3 && inEllipse(u, v, DOME) <= 1.12) {
    const fold = Math.floor((v - m1) / 3.6);
    const into = (v - m1) % 3.6;
    if (into > 2.8) return 't';
    if (into < 1 && u < 8 + fold * 3) return 'w';
    return u > m2 - 3 ? 't' : 'e';
  }
  // The strap round the helmet at the goggles' height.
  const dome = inEllipse(u, v, DOME);
  if (dome <= 1 && Math.abs(v + 3) < 1.3 && u < GOGGLES[0][0]) return v < -3 ? 'n' : 'N';
  // The helmet: leather, stitched down its crown, a seam round its brow.
  if (dome <= 1) {
    const l = domeLight(u, v, DOME, lx, ly);
    // A racing stripe in the team's colour over its crown, back to brow, stitched along its edges.
    if (dome > 0.5 && dome < 0.8 && v < -5 && u < 12) {
      if (dome < 0.55 || dome > 0.75) return Math.floor(u + v) % 2 === 0 ? 'e' : 'n';
      return l > 0.6 ? 'c' : 'b';
    }
    if (dome > 0.86) return l > 0.5 ? 'n' : 'N';
    return leather(l);
  }
  // The ear flap, buckled under.
  const flap = inEllipse(u, v, FLAP);
  if (flap <= 1) {
    if (Math.abs(v - 14) < 1.1 && Math.abs(u - FLAP[0]) < 2.4) return u < FLAP[0] ? 'y' : 'Y';
    return flap > 0.7 ? 'N' : u < FLAP[0] - 2 ? 'm' : 'n';
  }
  return null;
};

// --- The bomber jacket -----------------------------------------------------------------------------------------------

/** The jacket standing (the frame's x 5-56, y 39-57): brown leather, a fleece collar, a zip, a roundel, brass wings. */
function jacket(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || x > 41;
      // Creases in the leather.
      const crease = (x * 5 + y * 3) % 17 === 0;
      at(x, y, lit ? 'm' : shade ? 'N' : crease ? 'm' : 'n');
    }
  // The fleece collar: thick cream lumps round the neck and over the shoulders.
  for (let y = 41; y <= 45; y++)
    for (let x = 14; x <= 46; x++) {
      const d = Math.abs(x - 30);
      if (y > 41 + Math.max(0, 4 - d * 0.18) + (d < 4 ? 2 : 0)) continue;
      at(x, y, (x + y) % 3 === 0 ? 'w' : x > 40 ? 't' : 'e');
    }
  // The zip down its front.
  for (let y = 45; y <= 55; y++) at(30, y, y % 2 ? '5' : '4');
  // The roundel on its near shoulder: the team's colours round a white ring.
  for (let y = 43; y <= 51; y++)
    for (let x = 8; x <= 17; x++) {
      const d = Math.hypot(x + 0.5 - 12.5, y + 0.5 - 47);
      if (d < 1.3) at(x, y, 'c');
      else if (d < 2.6) at(x, y, 'w');
      else if (d < 3.9) at(x, y, 'b');
      else if (d < 4.4) at(x, y, 'B');
    }
  // Brass wings pinned on its chest.
  c.stamp(
    `
    yyy.y..y.yyy
    .YYYYyyYYYY.
    ...UUYYUU...
  `,
    32 - x0,
    46 - y0,
  );
  // The belt.
  for (let x = 16; x <= 43; x++) {
    at(x, 54, x <= 18 ? 'n' : 'N');
    at(x, 55, x > 40 ? 'N' : 'k');
  }
  c.stamp(
    `
    YyY
    U.U
  `,
    29 - x0,
    54 - y0,
  );
  return [c, x0, y0];
}

/** The jacket lying (right of the legs, under the head): the belt (left), the zip, the fleece collar by the head. */
const DEAD_JACKET = grid(`
  ....mmee
  ...mnnwe
  kNmnnnet
  kNnnnnew
  kNnnyYet
  kNmnYUee
  kN54545w
  kNnnnnet
  kNnmnnew
  kNnnnbet
  kNnnbwbe
  kNnnnbet
  kNnnnnew
  kNmnnnet
  kNnnnnte
  kNnnnnet
  kNNNNNtt
  ....NNNN
`);

/** The team's trousers, brown flying boots. */
const JACKET = (): Outfit => ({ torso: jacket(), dead: [DEAD_JACKET, 24, 51], legs: { n: 'n', N: 'N', m: 'm' } });

// --- The Sky Ace's dope and varnish on every other gun ---------------------------------------------------------------

/** The biplane finish (the default gun is its own, `pollinator_barnstormer`): red dope for steel, its wood and brass kept. */
const DOPED = { '3': 'R', '4': 'r', '5': 'i' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const SILVER = 0xc0cbdc;
const GREY = 0x8b9bb4;
const RED = 0xe43b44;
const DARK_RED = 0xa22633;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;

/** Rows of characters from a drawing (its indentation dropped). */
const rows = (g: string) =>
  g
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length)
    .join('\n');

/**
 * The propeller on its mast (31 × 8, the hub's middle at (15, 2), the mast down to the crown at row 7), spinning: its
 * two blades seen from the side as a bar across two rows thick, long when broadside, short as they turn toward us; a
 * pale blur where they swept.
 */
function blades(reach: number, far: 1 | -1): string {
  const out = Array.from({ length: 8 }, () => Array(31).fill('.') as string[]);
  const put = (x: number, y: number, k: string) => {
    (out[y] as string[])[x] = k;
  };
  for (let x = 0; x < 31; x++) {
    const d = x - 15;
    // The blur of its sweep: a broken silver band under it.
    if (Math.abs(d) <= 14 && Math.abs(d) > 1 && Math.abs(d) % 3 !== 1) put(x, 4, 's');
    if (Math.abs(d) > reach) continue;
    const tipped = Math.abs(d) > reach - 3;
    put(x, 1, tipped ? 'y' : d * far > 0 ? 'i' : 'r');
    put(x, 2, tipped ? 'Y' : d * far > 0 ? 'r' : 'R');
  }
  // The hub cap and the mast down to the crown.
  for (const [x, y, k] of [
    [14, 0, 'y'],
    [15, 0, 'y'],
    [14, 1, 'Y'],
    [15, 1, 'y'],
    [14, 2, 'U'],
    [15, 2, 'Y'],
    [14, 3, 'U'],
    [15, 3, 'Y'],
  ] as const)
    put(x, y, k);
  for (let y = 4; y < 8; y++) {
    put(14, y, 'g');
    put(15, y, 'G');
  }
  return out.map((r) => r.join('')).join('\n');
}

/** A turn: broadside, turning, end on, turning back, broadside the other way round. */
const SPIN = [
  blades(14, 1),
  blades(8, 1),
  blades(2, 1),
  blades(8, -1),
  blades(14, -1),
  blades(8, -1),
  blades(2, -1),
  blades(8, 1),
];
const propeller: Glyph = {
  kind: 'glyph',
  // The mast's foot on the hub (the frame's (31, 6)).
  at: { on: 'head', x: 31 - 15, y: -1 },
  frames: SPIN,
  tones: { r: RED, R: DARK_RED, i: 0xf6757a, y: YELLOW, Y: GOLD, U: 0xd77643, s: SILVER, g: SILVER, G: GREY },
  hold: [0.035, 0.035],
  order: 'cycle',
  layer: 'front',
};

/** A glint wiping across the lenses. */
const GLINT = ['w..', '.w.', '..w', 'w.w'].map((r) => rows(`${r}\n${r.split('').reverse().join('')}`));

const FLAIR: SetFlair = {
  head: [
    propeller,
    {
      kind: 'glyph',
      at: { on: 'head', x: 33, y: 22 },
      frames: GLINT,
      tones: { w: WHITE },
      hold: [0.06, 0.06],
      order: 'cycle',
      shown: 0.25,
      box: [12, 4],
      add: true,
      layer: 'front',
    },
    // The lenses glowing in the team's light.
    {
      kind: 'pulse',
      at: { on: 'head', x: 39, y: 25 },
      r: [5, 9],
      alpha: [0.15, 0.35],
      period: 1.6,
      tone: 'teamLight',
      layer: 'front',
    },
  ],
  outfit: [
    // Puffs of cloud left behind it on the run, swelling and fading.
    {
      kind: 'emit',
      at: { on: 'torso', x: 12, y: 50 },
      box: [6, 6],
      rate: 0,
      run: 0.05,
      v: [-6, 0, -6, -2],
      drag: 0.5,
      life: [0.6, 0.9],
      size: [3, 6],
      tones: [WHITE, WHITE, SILVER],
      alpha: [0.85, 0],
      layer: 'back',
    },
    // A glint on its wings.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 38, y: 47 },
      box: [10, 2],
      n: 1,
      period: [0.6, 1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  gun: [
    // Its propeller's blur at the muzzle and the exhaust puffing off its cowling.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 60, y: 20 },
      r: [3, 5],
      alpha: [0.15, 0.3],
      period: 0.3,
      tone: SILVER,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 44, y: 12 },
      box: [2, 2],
      rate: 2.5,
      v: [-14, -6, -10, -4],
      drag: 0.5,
      life: [0.35, 0.6],
      size: [2, 4],
      tones: [SILVER, GREY],
      alpha: [0.8, 0],
      layer: 'gun',
    },
  ],
};

/** The Barnstormer set (ADR-0160 amended). */
export const BARNSTORMER = drawnLater({ head: () => mask(helmet), outfit: JACKET, gun: DOPED, flair: FLAIR });
